// Pure helpers of the « Cartographie du système » page (lot CRM-3): rights, labels in business words, how a relation reads, freshness.
// The server decides what exists in a snapshot; nothing here interprets code. Tested in architecture.test.js.
import { hasPermission } from "./permissions";

export const canOpenArchitecture = (user) => !!user?.features?.architecture && hasPermission(user, "architecture:view");

export const KIND_LABELS = {
  api_endpoint: "Route d'API", ui_page: "Page", ui_route: "Route d'interface", collection: "Collection", permission: "Droit", event: "Événement",
  event_category: "Sujet de notification", state_table: "Table d'états", background_task: "Tâche de fond", file_context: "Fichiers privés",
  py_function: "Fonction Python", py_class: "Classe Python", pydantic_model: "Modèle de données", js_function: "Fonction JavaScript",
  ui_component: "Composant", hook: "Hook", api_call: "Appel d'API (interface)", db_index: "Index", env_var: "Variable d'environnement",
  file: "Fichier", ci_job: "Job de CI", infra_service: "Service Docker", lifecycle_hook: "Démarrage / arrêt", dependency_cycle: "Cycle d'imports",
};
// The kinds worth offering as a filter, in the order a reader looks for them.
export const FILTER_KINDS = ["api_endpoint", "ui_page", "collection", "permission", "event", "state_table", "background_task", "file_context", "env_var", "py_function", "ui_component", "hook", "file"];
export const kindLabel = (kind) => KIND_LABELS[kind] || kind;

export const CONFIDENCE_LABELS = { static: "lu dans le code", inferred: "déduit", unknown: "non tranché" };
export const CONFIDENCE_HELP = {
  static: "Lu directement dans le code (un appel nommé, une opération sur une collection, une route déclarée).",
  inferred: "Déduit : chaîne d'appels, rapprochement de chemins ou convention de nommage. Probable, pas prouvé ligne à ligne.",
  unknown: "L'analyse n'a pas pu trancher : l'élément figure dans « À investiguer ».",
};
export const confidenceLabel = (value) => CONFIDENCE_LABELS[value] || "non tranché";

// How a relation reads from the open element, by direction (« out » = it points to the other, « in » = the other points to it).
const PHRASES = {
  calls: ["Appelle", "Appelée par"], reads_collection: ["Lit", "Lue par"], writes_collection: ["Écrit dans", "Écrite par"],
  guarded_by: ["Protégé par le droit", "Protège"], triggers_event: ["Déclenche l'événement", "Déclenché par"], emits_event: ["Émet l'événement", "Émis par"],
  rendered_by: ["Message WhatsApp", "Message de l'événement"], belongs_to_category: ["Sujet", "Événements du sujet"],
  uses_endpoint: ["Utilise la route", "Utilisée par la page"], targets_endpoint: ["Appelle la route", "Appelée par l'interface"],
  tests_endpoint: ["Teste la route", "Testée par"], tests_file: ["Teste le fichier", "Testé par"], handled_by: ["Traitée par", "Traite la route"],
  grants_reads: ["Permet de lire", "Lisible avec le droit"], grants_writes: ["Permet d'écrire dans", "Modifiable avec le droit"],
  runs: ["Lance", "Lancée par"], runs_file: ["Lance le script", "Lancé par"], registers_file_context: ["Déclare les fichiers privés", "Déclarés par"],
  declares_page: ["Déclare la page", "Déclarée par"], declares_route: ["Déclare la route", "Déclarée par"], renders: ["Affiche", "Affiché par"],
  imports: ["Importe", "Importé par"], defines: ["Définit", "Défini dans"], uses_collection: ["Utilise la collection", "Utilisée par le fichier"],
  checks_permission: ["Vérifie le droit", "Vérifié par"], starts: ["Démarre", "Démarrée par"], indexes: ["Indexe", "Indexée par"],
  reads_env: ["Lit la variable", "Lue par"], references_env: ["Cite la variable", "Citée par"], declares_env: ["Déclare la variable", "Déclarée par"],
  calls_api: ["Appelle l'API", "Appelée par le fichier"], includes: ["Contient", "Fait partie du cycle"], needs: ["Attend le job", "Attendu par"],
  builds_from: ["Construit depuis", "Construit le service"], depends_on: ["Dépend du service", "Service requis par"], calls_workflow: ["Appelle le workflow", "Appelé par"],
};
export function relationTitle(kind, direction) {
  const phrase = PHRASES[kind];
  return phrase ? phrase[direction === "in" ? 1 : 0] : kind.replace(/_/g, " ");
}
// What a reader wants first: data, rights and events before the plumbing (imports, definitions).
const ORDER = ["calls", "handled_by", "reads_collection", "writes_collection", "guarded_by", "triggers_event", "emits_event", "rendered_by", "belongs_to_category", "uses_endpoint", "targets_endpoint",
  "grants_reads", "grants_writes", "tests_endpoint", "runs", "registers_file_context", "declares_page", "renders"];
const rank = (kind) => { const i = ORDER.indexOf(kind); return i === -1 ? ORDER.length : i; };

export function sortGroups(groups) {
  return [...(groups || [])].sort((a, b) => (a.direction === b.direction ? rank(a.kind) - rank(b.kind) || a.kind.localeCompare(b.kind) : a.direction === "out" ? -1 : 1));
}

export const sourceText = (path, line) => (path ? (line ? `${path}:${line}` : path) : "");

export function relationProof(relation) {
  const { path, line } = relation?.evidence || {};
  return sourceText(path, line);
}

// A transversal collection (rights, journal, settings) is reached by almost every request: it is shown, but marked.
export const isTransversal = (relation) => !!relation?.meta?.transversal;

export function viaText(relation) {
  const via = relation?.meta?.via;
  return via ? via.split(" › ").join(" → ") : "";
}

// --- Snapshot freshness ----------------------------------------------------------------------------------------------------------------
export function freshnessNotices(snapshot) {
  if (!snapshot) return [];
  const notices = [];
  if (snapshot.stale) notices.push({ tone: "warning", text: `Cet instantané a ${snapshot.age_days} jours : la carte peut ne plus refléter le code.` });
  if (snapshot.matches_running_version === false) notices.push({ tone: "warning", text: "Cet instantané ne correspond pas à la version en cours d'exécution." });
  return notices;
}
export const shortSha = (sha) => (sha ? String(sha).slice(0, 12) : "");
export function ageText(days) {
  if (days === null || days === undefined) return "date inconnue";
  if (days === 0) return "publié aujourd'hui";
  return days === 1 ? "publié hier" : `publié il y a ${days} jours`;
}

// --- Page address (shareable) ---------------------------------------------------------------------------------------------------------
export const TABS = [["overview", "Architecture globale"], ["domains", "Domaines"], ["explorer", "Explorateur"], ["unknowns", "À investiguer"], ["coverage", "Couverture"], ["versions", "Versions"]];
const KEYS = ["q", "kind", "confidence", "module", "node", "domain", "explore", "tab", "snapshot", "view", "from", "to"];

export function readParams(search) {
  const params = new URLSearchParams(search);
  const out = {};
  KEYS.forEach((key) => { out[key] = params.get(key) || ""; });
  if (!TABS.some(([id]) => id === out.tab)) out.tab = "overview";
  if (out.view !== "list") out.view = "graph";
  return out;
}

export function writeParams(current, changes) {
  const merged = { ...current, ...changes };
  const params = new URLSearchParams();
  KEYS.forEach((key) => {
    const value = merged[key];
    if (!value || (key === "tab" && value === "overview") || (key === "view" && value === "graph")) return;
    params.set(key, value);
  });
  return params.toString();
}

export const searching = (params) => params.q.trim().length >= 2 || !!params.kind || !!params.module || !!params.confidence;

export function architectureError(error, fallback) {
  const status = error?.response?.status;
  if (status === 404) return "Aucun instantané n'est disponible pour cette demande.";
  if (status === 403) return "Vous n'avez pas le droit de consulter la cartographie.";
  return fallback;
}

// Short label of a domain (« backend/finance » → « finance », « frontend/src/pages » → « pages (interface) »).
export function moduleLabel(id) {
  if (!id) return "(sans domaine)";
  if (id.startsWith("backend/")) return id.slice("backend/".length);
  if (id.startsWith("frontend/src/")) return `${id.slice("frontend/src/".length)} (interface)`;
  return id;
}
export function moduleGroup(id) {
  if (!id) return "Autres";
  if (id.startsWith("backend/") || id === "backend") return "Serveur";
  if (id.startsWith("frontend/") || id === "frontend") return "Interface";
  return "Autres";
}
export const groupModules = (modules) => {
  const groups = {};
  (modules || []).forEach((m) => { (groups[moduleGroup(m.id)] ||= []).push(m); });
  return ["Serveur", "Interface", "Autres"].filter((g) => groups[g]).map((g) => [g, groups[g]]);
};

// The kinds a domain tile shows, most informative first.
export const TILE_KINDS = ["api_endpoint", "collection", "permission", "ui_page", "event", "background_task"];
export const tileCounts = (module) => TILE_KINDS.filter((k) => module.counts?.[k]).map((k) => [k, module.counts[k]]);

// --- Facts shown for one element ------------------------------------------------------------------------------------------------------
const yesNo = (value) => (value ? "oui" : "non");
const gateText = (gate) => (gate.keys?.length ? `${gate.gate}(${gate.keys.join(", ")})` : gate.gate);
const unique = (items) => [...new Set(items)];

// Label / value pairs read from the element's metadata, by kind. Only what the registry actually holds; an absent field is simply not shown.
export function nodeFacts(node) {
  const m = node?.metadata || {};
  const facts = [];
  const add = (label, value) => { if (value !== undefined && value !== null && value !== "" && !(Array.isArray(value) && !value.length)) facts.push([label, Array.isArray(value) ? value.join(", ") : String(value)]); };
  switch (node?.kind) {
    case "api_endpoint":
      add("Méthode", m.method); add("Chemin", m.path_pattern); add("Fonction", m.handler);
      add("Gardes", unique((m.gates || []).map(gateText))); if (m.mounted === false) add("Rattachement", "routeur non rattaché à l'application");
      if (m.no_data_access_resolved) add("Données", "aucun accès à une collection résolu");
      break;
    case "ui_page": add("Gardes de la page", Object.entries(m.guards || {}).map(([k, v]) => `${k} = ${[].concat(v).join(", ")}`)); add("Composants", m.components); break;
    case "collection": if (m.transversal) add("Particularité", "traversée par presque toutes les requêtes (droits, journal, réglages)"); break;
    case "permission": add("Catégorie", m.category); add("Réservé à l'administrateur", m.admin_only ? "oui" : null); add("Définie dans le catalogue", yesNo(m.defined)); break;
    case "event": add("Réglage", m.setting); add("Sujet", m.category); add("Dans la cloche", yesNo(m.in_app)); break;
    case "state_table": add("Entrées", m.entries); add("Avec transitions", yesNo(m.has_transitions)); break;
    case "file": add("Catégorie", m.category); add("Langage", m.language); add("Taille", m.size ? `${m.size} octets` : null); add("Analyse", m.analysed === false ? "non analysé" : null); add("Motif d'exclusion", m.reason);
      if (m.orphan_presumed) add("Particularité", "aucun import depuis un point d'entrée (orphelin présumé)"); break;
    case "background_task": add("Lancée par", m.api); break;
    case "env_var": add("Interrupteur de module", m.feature_flag ? "oui" : null); break;
    case "file_context": add("Nom résolu", m.resolved === false ? "non" : null); break;
    case "dependency_cycle": add("Fichiers", m.size); add("Lecture", m.variant === "strict" ? "imports directs" : "sans server.py"); break;
    default: break;
  }
  return facts;
}

// Transitions of a state table, ready to show: « état → états suivants ».
export const transitionRows = (node) => Object.entries(node?.metadata?.transitions || {}).map(([from, to]) => [from, [].concat(to || [])]);

// --- Overview by domain (layers: interface → server domains → data, workers, notifications) -------------------------------------------------
export const domainLabel = (id) => (id === "interface" ? "Interface React" : id.startsWith("backend/") ? id.slice("backend/".length) : id);
export const INITIAL_DOMAINS = 12;
export const plural = (n, one, many) => `${n} ${n > 1 ? many : one}`;

// The chips of a domain card: only what is not zero, most informative first.
export function domainChips(m) {
  const chips = [];
  if (m.writes) chips.push(`écrit ${plural(m.writes, "collection", "collections")}`);
  else if (m.reads) chips.push(`lit ${plural(m.reads, "collection", "collections")}`);
  if (m.events) chips.push(plural(m.events, "événement", "événements"));
  if (m.tasks) chips.push(plural(m.tasks, "tâche de fond", "tâches de fond"));
  return chips;
}
export const visibleDomains = (modules, all) => (all ? modules : modules.slice(0, INITIAL_DOMAINS));

// --- Explorer (lineage graph) -----------------------------------------------------------------------------------------------------------
export const KIND_COLORS = {
  ui_page: "#2563eb", ui_route: "#60a5fa", ui_component: "#93c5fd", hook: "#93c5fd", api_call: "#38bdf8", js_function: "#bae6fd",
  api_endpoint: "#0d9488", py_function: "#5eead4", py_class: "#99f6e4", pydantic_model: "#99f6e4",
  collection: "#6366f1", db_index: "#a5b4fc", state_table: "#c4b5fd", file_context: "#c4b5fd",
  event: "#f59e0b", event_category: "#fcd34d", permission: "#e11d48", background_task: "#a855f7", env_var: "#94a3b8", file: "#cbd5e1",
};
export const kindColor = (kind) => KIND_COLORS[kind] || "#94a3b8";

// Lanes of the explorer: the system read from left to right, as a person tells it (what is on screen, what the server does, who may, where it ends up).
export const LANES = [
  { id: "interface", label: "01 / Interface", accent: "#2563eb", icon: "monitor", kinds: ["ui_page", "ui_route", "ui_component", "hook", "api_call", "js_function"] },
  { id: "server", label: "02 / Serveur", accent: "#0d9488", icon: "server", kinds: ["api_endpoint", "py_function", "py_class", "pydantic_model", "background_task", "lifecycle_hook"] },
  { id: "rules", label: "03 / Droits et règles", accent: "#e11d48", icon: "shield", kinds: ["permission", "state_table", "file_context", "env_var"] },
  { id: "data", label: "04 / Données et événements", accent: "#6366f1", icon: "database", kinds: ["collection", "db_index", "event", "event_category"] },
  { id: "other", label: "05 / Autres", accent: "#64748b", icon: "file", kinds: [] },
];
export const laneOf = (kind) => LANES.find((l) => l.kinds.includes(kind)) || LANES[LANES.length - 1];
export const LANE_GEOMETRY = { nodeW: 196, nodeH: 54, gapX: 28, gapY: 22, laneGap: 40, pad: 28, head: 44, maxRows: 8 };
// Positions of the cards: one band per lane that has something in it, cards stacked by distance from the starting point (upstream first), a new column
// when a lane gets long. Pure: the same graph always gives the same picture.
export function laneLayout(graph, levels = {}) {
  const g = LANE_GEOMETRY;
  const byLane = new Map();
  Object.values(graph.nodes).forEach((n) => { const lane = laneOf(n.kind); if (!byLane.has(lane.id)) byLane.set(lane.id, []); byLane.get(lane.id).push(n); });
  const positions = {}; const bands = [];
  let x = 0; let tallest = 0;
  LANES.filter((l) => byLane.has(l.id)).forEach((lane) => {
    const nodes = byLane.get(lane.id).sort((a, b) => (levels[a.id] ?? 99) - (levels[b.id] ?? 99) || a.name.localeCompare(b.name));
    const columns = Math.max(1, Math.ceil(nodes.length / g.maxRows));
    const rows = Math.ceil(nodes.length / columns);
    const width = g.pad * 2 + columns * g.nodeW + (columns - 1) * g.gapX;
    nodes.forEach((n, i) => {
      const col = Math.floor(i / rows); const row = i % rows;
      positions[n.id] = { x: x + g.pad + col * (g.nodeW + g.gapX) + g.nodeW / 2, y: g.head + g.pad + row * (g.nodeH + g.gapY) + g.nodeH / 2 };
    });
    const height = g.head + g.pad * 2 + rows * g.nodeH + (rows - 1) * g.gapY;
    tallest = Math.max(tallest, height);
    bands.push({ id: lane.id, label: lane.label, accent: lane.accent, x, width, height, count: nodes.length });
    x += width + g.laneGap;
  });
  bands.forEach((b) => { b.height = tallest; });  // one common height: the lanes read as the same frame
  return { positions, bands };
}
export const shortName = (name, max = 30) => (name.length > max ? `${name.slice(0, max - 1)}…` : name);
export const edgeKey = (edge) => `${edge.from}|${edge.kind}|${edge.to}`;

export const emptyGraph = () => ({ nodes: {}, edges: {} });
// Merging keeps what is already drawn and refreshes the counters of what is still to open.
export function mergeGraph(graph, nodes = [], edges = []) {
  const next = { nodes: { ...graph.nodes }, edges: { ...graph.edges } };
  nodes.forEach((n) => { next.nodes[n.id] = { ...(next.nodes[n.id] || {}), ...n }; });
  edges.forEach((e) => { if (next.nodes[e.from] && next.nodes[e.to]) next.edges[edgeKey(e)] = e; });
  return next;
}
// What is left to open for one element: its neighbours in the whole map minus the ones already drawn (the server gives the whole count, `degree`).
export function hiddenOf(graph, node) {
  const drawn = { in: new Set(), out: new Set() };
  Object.values(graph.edges).forEach((e) => { if (e.from === node.id) drawn.out.add(e.to); if (e.to === node.id) drawn.in.add(e.from); });
  return { in: Math.max(0, (node.degree?.in ?? 0) - drawn.in.size), out: Math.max(0, (node.degree?.out ?? 0) - drawn.out.size) };
}
export function nodeLabel(node, hidden = { in: 0, out: 0 }) {
  const extra = hidden.in + hidden.out;
  return `${shortName(node.name, 26)}${extra ? `  +${extra}` : ""}`;
}
// Levels from the starting point of the explorer, whatever calls drew the picture: downstream = following the relations, upstream = against them.
export function computeLevels(graph, startId) {
  const levels = { [startId]: 0 };
  [["down", 1], ["up", -1]].forEach(([side, sign]) => {
    const next = {};
    Object.values(graph.edges).forEach((e) => { const [a, b] = side === "down" ? [e.from, e.to] : [e.to, e.from]; (next[a] ||= []).push(b); });
    const queue = [startId]; const seen = new Set([startId]);
    while (queue.length) {
      const current = queue.shift();
      (next[current] || []).forEach((other) => { if (!seen.has(other)) { seen.add(other); if (!(other in levels)) levels[other] = sign * (Math.abs(levels[current]) + 1); queue.push(other); } });
    }
  });
  return levels;
}
export const legendKinds = (graph) => {
  const counts = {};
  Object.values(graph.nodes).forEach((n) => { counts[n.kind] = (counts[n.kind] || 0) + 1; });
  return Object.entries(counts).sort((a, b) => b[1] - a[1]).map(([kind, count]) => ({ kind, count }));
};
export const listRows = (graph, hiddenKinds = [], levels = {}) => Object.values(graph.nodes).filter((n) => !hiddenKinds.includes(n.kind))
  .sort((a, b) => (levels[a.id] ?? 99) - (levels[b.id] ?? 99) || a.name.localeCompare(b.name));
export const levelText = (level) => (level === undefined ? "relié par un chemin" : level === 0 ? "point de départ" : level > 0 ? `en aval (${level})` : `en amont (${-level})`);
export const pathModeText = (mode) => ({ down: "le premier appelle, en passant par les éléments ci-dessous, le second", up: "le second appelle, en passant par les éléments ci-dessous, le premier", any: "reliés dans les deux sens" }[mode] || "");

// --- Dynamics and versions (lot CRM-4) -----------------------------------------------------------------------------------------------
export const DIFF_GROUP_LABELS = {
  files: "Fichiers", symbols: "Fonctions et composants", routes: "Routes et pages", models: "Données et modèles", permissions: "Droits",
  states: "États et transitions", events: "Événements", tasks: "Tâches de fond et déploiement", dependencies: "Dépendances et configuration", other: "Autres",
};
export const DIFF_GROUP_ORDER = ["routes", "permissions", "models", "states", "events", "tasks", "dependencies", "symbols", "files", "other"];
export const CHANGE_LABELS = { added: "Ajouté", removed: "Supprimé", renamed: "Renommé", modified: "Modifié" };
export const CHANGE_TONES = {
  added: "bg-emerald-50 text-emerald-800 border-emerald-200", removed: "bg-rose-50 text-rose-800 border-rose-200",
  renamed: "bg-sky-50 text-sky-800 border-sky-200", modified: "bg-amber-50 text-amber-800 border-amber-200",
};
export const SCAN_STATUS_LABELS = {
  requested: "En attente", running: "En cours", published: "Publié", unchanged: "Sans changement", failed: "Échec", cancelled: "Annulé", rolled_back: "Retour à une version",
};
export const SCAN_MODE_LABELS = { incremental: "Incrémentale", full: "Complète" };
export const scanIsOpen = (scan) => scan?.status === "requested" || scan?.status === "running";

// A rename is only called « prouvé » when Git's own content fingerprint says so; anything else is « probable », never asserted.
export function changeLabel(item) {
  if (item.change !== "renamed") return CHANGE_LABELS[item.change] || item.change;
  return item.certainty === "prouvé" ? "Renommé (prouvé)" : "Renommé probablement";
}

const DELTA_KEYS = {
  gates: "portes", keys: "clés", fields: "champs", handler: "fonction de traitement", path: "fichier", module: "domaine", status: "statut", confidence: "confiance",
  method: "méthode", entries: "entrées", transitions: "transitions", components: "composants", guards: "gardes", unique: "unicité", collection: "collection",
  category: "catégorie", setting: "réglage", in_app: "cloche", admin_only: "réservé à l'administrateur", is_async: "asynchrone",
};
const show = (value) => (value && typeof value === "object" ? JSON.stringify(value) : String(value ?? "—"));

// One short line per changed characteristic: names only, never a value the registry does not hold.
export function deltaLines(delta) {
  return Object.entries(delta || {}).map(([key, change]) => {
    const label = DELTA_KEYS[key] || key;
    if (change.changed) return `${label} : modifié`;
    if ("added" in change || "removed" in change) {
      const added = (change.added || []).map(show).join(", ");
      const removed = (change.removed || []).map(show).join(", ");
      return `${label} : ${[added && `+ ${added}`, removed && `− ${removed}`].filter(Boolean).join(" · ")}`;
    }
    return `${label} : ${show(change.from)} → ${show(change.to)}`;
  });
}

export function totalsOf(summary) {
  const out = { added: 0, removed: 0, renamed: 0, modified: 0 };
  Object.values(summary || {}).forEach((counts) => Object.keys(out).forEach((k) => { out[k] += counts[k] || 0; }));
  return out;
}
export const isUnchanged = (diff) => !!diff && diff.same_content;

export function lagNotice(lastScan) {
  if (!lastScan?.behind) return null;
  const when = lastScan.finished_at ? new Date(lastScan.finished_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "récemment";
  return `La dernière mise à jour (${when}) a échoué : la carte montre encore la dernière version valide, qui peut être en retard sur le code.`;
}
